import { FC, useMemo } from "react";
import { Box, Button, Chip, MenuItem, Stack, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  TVehicleFilterOptions,
  TVehicleListFilters,
} from "../../../api/vehicule";
import useStyles from "./styles";
import { translateVehicleStatus } from "../../../utils/types";

interface Props {
  filters: TVehicleListFilters;
  options: TVehicleFilterOptions;
  onChange: (partial: Partial<TVehicleListFilters>) => void;
  onReset: () => void;
  /** Total rows the current filter combination matches. */
  resultCount?: number;
}

/**
 * Status labels live in utils/types.ts (VEHICLE_STATUS_LABELS) so the filter
 * dropdown, the list chip, the detail button and the dashboard counters all
 * read the same table. This file used to keep a private copy of that map,
 * which is how the dropdown could say "Available" while the chip in the same
 * row still said "LIBRE".
 */

/**
 * Filter bar for the vehicle list.
 *
 * Every control maps to a server-side parameter. Filtering client-side would
 * only ever search the 25 rows of the current page, which on a 2000-vehicle
 * fleet is a search that quietly lies.
 *
 * Options and counts come from the API rather than being hardcoded here, so
 * the dropdowns cannot offer a brand the fleet does not own or miss one added
 * this morning.
 */
export const VehicleFilters: FC<Props> = ({
  filters,
  options,
  onChange,
  onReset,
  resultCount,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const activeCount = useMemo(
    () =>
      [
        filters.status,
        filters.brand_id,
        filters.carburant,
        filters.department_id,
        filters.assigned,
        filters.contract_end_before,
      ].filter((v) => v !== undefined && v !== null && v !== "").length,
    [filters]
  );

  const statusLabel = (value: string) => translateVehicleStatus(t, value);

  return (
    <Box className={classes.filtersBar}>
      <Box className={classes.filtersGrid}>
        <TextField
          select
          size="small"
          label={t("vehicule.filterStatus", "Statut")}
          value={filters.status ?? ""}
          onChange={(e) => onChange({ status: e.target.value })}
        >
          <MenuItem value="">{t("vehicule.allStatuses", "Tous les statuts")}</MenuItem>
          {options.statuses.map((o) => (
            <MenuItem key={String(o.value)} value={String(o.value)}>
              {statusLabel(String(o.value))} ({o.count})
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label={t("vehicule.filterBrand", "Marque")}
          value={filters.brand_id ?? ""}
          onChange={(e) =>
            onChange({ brand_id: e.target.value ? Number(e.target.value) : null })
          }
        >
          <MenuItem value="">{t("vehicule.allBrands", "Toutes les marques")}</MenuItem>
          {options.brands.map((o) => (
            <MenuItem key={String(o.value)} value={String(o.value)}>
              {o.label} ({o.count})
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label={t("vehicule.filterFuel", "Carburant")}
          value={filters.carburant ?? ""}
          onChange={(e) => onChange({ carburant: e.target.value })}
        >
          <MenuItem value="">{t("vehicule.allFuels", "Tous les carburants")}</MenuItem>
          {options.fuels.map((o) => (
            <MenuItem key={String(o.value)} value={String(o.value)}>
              {String(o.value)} ({o.count})
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label={t("vehicule.filterDepartment", "Département")}
          value={filters.department_id ?? ""}
          onChange={(e) =>
            onChange({
              department_id: e.target.value ? Number(e.target.value) : null,
            })
          }
        >
          <MenuItem value="">
            {t("vehicule.allDepartments", "Tous les départements")}
          </MenuItem>
          {options.departments.map((o) => (
            <MenuItem key={String(o.value)} value={String(o.value)}>
              {o.label} ({o.count})
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label={t("vehicule.filterAssigned", "Affectation")}
          value={filters.assigned ?? ""}
          onChange={(e) =>
            onChange({ assigned: e.target.value as "yes" | "no" | "" })
          }
        >
          <MenuItem value="">{t("vehicule.allAssignments", "Toutes")}</MenuItem>
          <MenuItem value="yes">{t("vehicule.assignedYes", "Avec conducteur")}</MenuItem>
          <MenuItem value="no">{t("vehicule.assignedNo", "Sans conducteur")}</MenuItem>
        </TextField>

        <TextField
          select
          size="small"
          label={t("vehicule.filterSort", "Tri")}
          value={filters.sort ?? "plate_asc"}
          onChange={(e) =>
            onChange({ sort: e.target.value as TVehicleListFilters["sort"] })
          }
        >
          <MenuItem value="plate_asc">{t("vehicule.sortPlateAsc", "Immatriculation A→Z")}</MenuItem>
          <MenuItem value="plate_desc">{t("vehicule.sortPlateDesc", "Immatriculation Z→A")}</MenuItem>
          <MenuItem value="brand_asc">{t("vehicule.sortBrandAsc", "Marque A→Z")}</MenuItem>
          <MenuItem value="contract_end_asc">{t("vehicule.sortContractEndAsc", "Fin de contrat (proche)")}</MenuItem>
          <MenuItem value="contract_end_desc">{t("vehicule.sortContractEndDesc", "Fin de contrat (lointaine)")}</MenuItem>
          <MenuItem value="recent">{t("vehicule.sortRecent", "Ajout récent")}</MenuItem>
        </TextField>
      </Box>

      {/*
        Contract-expiry shortcuts.
        A fleet manager asks "what expires this quarter", not "let me pick two
        dates". The counts come from the API, so a bucket with nothing in it is
        visibly empty before it is clicked.
      */}
      {options.contract_expiring.length > 0 && (
        <Stack direction="row" spacing={1} className={classes.quickFilters} flexWrap="wrap">
          <span className={classes.quickLabel}>
            {t("vehicule.contractEnding", "Fin de contrat sous")}
          </span>
          {options.contract_expiring.map((bucket) => {
            const selected = filters.contract_end_before === bucket.until;
            return (
              <Chip
                key={bucket.days}
                size="small"
                clickable
                variant={selected ? "filled" : "outlined"}
                color={selected ? "primary" : "default"}
                // `days`, not `count`: i18next treats `count` as the plural
                // selector and would look up daysShort_one / daysShort_other
                // before falling back to the base key.
                label={`${t("common.daysShort", "{{days}} d", {
                  days: bucket.days,
                })} (${bucket.count})`}
                onClick={() =>
                  onChange({
                    // Clicking the active bucket clears it, so the chip is a
                    // toggle rather than a one-way trip.
                    contract_end_before: selected ? "" : bucket.until,
                    sort: selected ? filters.sort : "contract_end_asc",
                  })
                }
              />
            );
          })}
        </Stack>
      )}

      <Stack direction="row" alignItems="center" spacing={1.5} className={classes.filtersFooter}>
        {resultCount !== undefined && (
          <span className={classes.resultCount}>
            {t("vehicule.resultCount", {
              defaultValue: "{{count}} véhicule(s)",
              count: resultCount,
            })}
          </span>
        )}
        {activeCount > 0 && (
          <Button size="small" onClick={onReset}>
            {t("vehicule.resetFilters", "Réinitialiser")} ({activeCount})
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default VehicleFilters;
